import { registry } from './registry.ts';
import { Logger } from '@theidiomaclub/logging';
import { User } from '../domain/entities/User';



interface IMainFeature {
    init: () => Promise<void>;
}

export class MainRouter {
    private static _instance: MainRouter;
    private _currentView: string | null = null;
    private _loadedViews: Set<string> = new Set();
    private _viewOrder: Record<string, number> = { 'map': 0, 'rank': 1, 'admin': 2, 'crm': 3, 'profile': 4 };

    private constructor() {
        // History listeners removed — React Router (HashRouter) handles navigation.
        // MainRouter is now a utility for loadViewContent + initViewLogic only.
    }

    private _isSwitching = false;

    static getInstance(): MainRouter {
        if (!this._instance) {
            this._instance = new MainRouter();
        }
        return this._instance;
    }

    /**
     * Switch between main SPA views
     */
    async switchView(viewName: string, force: boolean = false): Promise<void> {
        // 🏰 CLAN OVERLAY INTERCEPT: Clan is a bottom sheet, not a page view
        if (viewName === 'clan') {
            const openClanSheet = (window as unknown as { openClanSheet?: () => void }).openClanSheet;
            if (openClanSheet) openClanSheet();
            // Keep current view active; if no view yet (first load), default to 'map'
            const fallback = this._currentView ?? 'map';
            window.history.replaceState(null, '', `#${fallback}`);
            if (!this._currentView) await this.switchView('map');
            return;
        }

        // 🏰 SUBSCRIPTION GUARD: Handled by <AccessGate /> in App.tsx route tree.
        // MainRouter no longer performs subscription checks — all React routes
        // (including VanillaViewBridge) pass through AccessGate first.

        // 🛡️ RBAC ROUTE GUARD: Block non-admins from admin panel
        if (viewName === 'admin') {
            const isAdmin = await this._checkAdminRole();
            if (!isAdmin) {
                Logger.warn('🔒 MainRouter: Non-admin user attempted to access admin panel. Redirecting to map.');
                const notify = registry.get<{ toast: (msg: string, type: string) => void }>('SmartNotify');
                notify?.toast('⚠️ No tienes permisos de administrador.', 'warning');
                await this.switchView('map', true);
                return;
            }
        }
        if (!force && viewName === this._currentView && document.querySelector(`#view-${viewName}.active`)) {
            return;
        }

        if (this._isSwitching) {
            Logger.debug(`MainRouter: Waiting for current transition to finish before switching to [${viewName}]...`);
            await new Promise(resolve => {
                const check = () => {
                    if (!this._isSwitching) resolve(true);
                    else setTimeout(check, 50);
                };
                check();
            });
        }
        this._isSwitching = true;

        Logger.info(`MainRouter: Switching to [${viewName}]`);

        const currentIndex = this._currentView ? (this._viewOrder[this._currentView] ?? 0) : 0;
        const nextIndex = this._viewOrder[viewName] ?? 0;
        const direction = nextIndex > currentIndex ? 'slide-left' : 'slide-right';

        // Update URL
        if (window.location.hash !== '#' + viewName && !window.location.hash.startsWith('#' + viewName + '/')) {
            history.pushState(null, '', '#' + viewName);
        }

        this._updateNavUI(viewName);

        // 🚀 IMMEDIATE UI HIDE FOR IMMERSIVE VIEWS (crm)
        const immersiveViews = ['crm'];
        if (immersiveViews.includes(viewName)) {
            const header = document.querySelector('.top-header') as HTMLElement;
            const bottomNav = document.querySelector('.bottom-nav-container') as HTMLElement;
            const container = document.getElementById('app-views');
            if (header) header.style.display = 'none';
            if (bottomNav) bottomNav.style.display = 'none';
            if (container) container.style.paddingTop = '0';
            // Also hide all views immediately
            document.querySelectorAll('.view-section').forEach(el => {
                (el as HTMLElement).style.display = 'none';
            });
        }

        // Load view content if not already loaded
        const loadPromise = this.loadViewContent(viewName);

        // Native View Transition API support
        type ViewTransitionDocument = Document & {
            startViewTransition?: (callback: () => Promise<void> | void) => { finished: Promise<void> };
        };
        const docWithTransition = document as ViewTransitionDocument;
        const useTransition = !!docWithTransition.startViewTransition;

        try {
            if (useTransition && docWithTransition.startViewTransition) {
                document.documentElement.setAttribute('data-transition', direction);
                await docWithTransition.startViewTransition(async () => {
                    this._performDOMSwitch(viewName);
                    await loadPromise;
                }).finished;
                document.documentElement.removeAttribute('data-transition');
            } else {
                Logger.debug(`MainRouter: Manual switch to ${viewName}`);
                this._performDOMSwitch(viewName);
                await loadPromise;
            }
        } finally {
            this._currentView = viewName;
            this._isSwitching = false;
        }
    }

    // 🏰 _checkSubscriptionExpired, _getAccessBlockReason, _showPaywallScreen
    // REMOVED — Subscription access control is now handled by <AccessGate /> in App.tsx.
    // See: frontend/features/shell/ui/AccessGate.tsx

    /**
     * 🛡️ RBAC Guard: Check if current user has admin role
     * Returns true for super_admin, admin, and teacher roles.
     */
    private async _checkAdminRole(): Promise<boolean> {
        try {
            const store = registry.get<{ get: (key: string) => User | undefined }>('Store');
            const userProfile = store?.get('user');

            if (!userProfile?.role) return false;

            const adminRoles = ['super_admin', 'admin', 'teacher'];
            return adminRoles.includes(userProfile.role);
        } catch (e) {
            Logger.error('MainRouter: Error checking admin role', e);
            return false;
        }
    }

    /**
     * Mark a view as already loaded
     */
    markViewAsLoaded(viewName: string): void {
        this._loadedViews.add(viewName);
        this._currentView = viewName;
    }

    /**
     * Preload essential views for initial paint (Map, Rank)
     * 🛡️ Only preloads if user is authenticated to avoid "User not authenticated" errors
     */
    async preloadEssentialViews(): Promise<void> {
        // 🚀 PERFORMANCE: Preload Rank module chunk in background during idle time
        // This ensures dynamic import() resolves instantly from memory when user taps Rank
        const preloadRank = () => {
            import('../../features/rank/ui/RankModule').catch(() => { /* silent — not critical */ });
        };

        if ('requestIdleCallback' in window) {
            (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(preloadRank);
        } else {
            setTimeout(preloadRank, 2000);
        }

        Logger.system('MainRouter: preloadEssentialViews() — Rank chunk queued for preload.');
    }

    /**
     * Load HTML content for a Vanilla view.
     * Public API for VanillaViewBridge + internal use.
     * If an external container is provided, content is loaded into it instead of the default #view-{name} section.
     */
    async loadViewContent(viewName: string, externalContainer?: HTMLElement): Promise<void> {
        // 🛡️ Admin is NEVER cached (VanillaViewBridge UNCACHEABLE_VIEWS) — always reload fresh
        if (viewName !== 'admin' && this._loadedViews.has(viewName)) return;

        const container = externalContainer ?? document.getElementById(`view-${viewName}`);
        if (!container) return;

        try {
            // NOTE: 'rank' is a pure React feature
            // mounted directly by React Router in App.tsx — they don't come here.

            // NOTE: 'clan' is no longer a page view — it's a bottom sheet overlay
            // triggered via window.openClanSheet() → dispatches 'clan:open' event (BottomNav listens)

            if (viewName === 'admin') {
                const { loadAdminModule } = await import('../../app-main');
                await (loadAdminModule as unknown as () => Promise<void>)();
                this._loadedViews.add(viewName);
                return;
            }

            // 🧬 LOKI-FIRED FIX: crm is a React view wrapped in an adapter. skip HTML fetch.
            const reactMountedViews = ['crm'];
            if (reactMountedViews.includes(viewName)) {
                this._loadedViews.add(viewName);
                return; // VanillaViewBridge will call initViewLogic() independently
            }

            // 🛡️ ROBUST FETCH WITH RETRY
            const fetchWithRetry = async (url: string, retries = 3): Promise<Response> => {
                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    return response;
                } catch (err) {
                    if (retries > 0) {
                        Logger.warn(`MainRouter: Fetch failed for ${url}, retrying... (${retries} left)`);
                        await new Promise(r => setTimeout(r, 1000));
                        return fetchWithRetry(url, retries - 1);
                    }
                    throw err;
                }
            };

            const resp = await fetchWithRetry(`/features/${viewName}/ui/${viewName}.view.html`);
            const html = await resp.text();

            if (html.includes('<!DOCTYPE html>')) {
                throw new Error(`Invalid view HTML for ${viewName} (received full HTML page)`);
            }

            container.innerHTML = html;
            this._loadedViews.add(viewName);

            // NOTE: initViewLogic is NOT called here — VanillaViewBridge calls it
            // separately after loadViewContent to prevent double-initialization.

        } catch (e) {
            Logger.error(`MainRouter: Fatal error loading ${viewName}:`, e);
            const notify = registry.get('SmartNotify');
            notify?.toast(`❌ Error al cargar [${viewName}]. Reintentando...`, 'error');

            // Auto-reload on stale chunks or network failures if it's a first-time load
            if (e instanceof TypeError && e.message === 'Failed to fetch') {
                Logger.warn('MainRouter: Connection lost or server down during fetch.');
            }
        }
    }

    /**
     * Initialize feature logic for a Vanilla view (dynamic imports + registry init).
     * Public API for VanillaViewBridge + internal use.
     */
    async initViewLogic(viewName: string): Promise<void> {
        try {
            /* El CRM se mudo al back office; esta app ya no lo monta. Si algo
               pide esta vista, el traspaso de `App.tsx` lo lleva a `/admin/`. */

            // All fully React features (profile, map, rank) no longer use initViewLogic.
            // Empty placeholder for any remaining Vanilla features that register in the registry.

            const featureNames: Record<string, string> = {};
            const featureName = featureNames[viewName];
            const feature = registry.get<IMainFeature>(featureName);

            if (feature?.init) {
                await feature.init();
            }
        } catch (e: unknown) {
            const err = e as Error;
            Logger.error(`MainRouter: Logic error for ${viewName}`, err);

            if (err.message && (
                err.message.includes('Failed to fetch dynamically imported module') ||
                err.message.includes('Importing a module script failed') ||
                err.message.includes('Strict MIME type checking') ||
                err.message.includes('responded with a MIME type of "text/html"')
            )) {
                Logger.warn('⚠️ Stale chunk detected. Reloading application...');
                const reloadKey = `retry_load_${viewName}`;
                if (!sessionStorage.getItem(reloadKey)) {
                    sessionStorage.setItem(reloadKey, 'true');
                    window.location.reload();
                    return;
                } else {
                    sessionStorage.removeItem(reloadKey);
                }
            }
        }
    }

    private _performDOMSwitch(viewName: string): void {
        Logger.debug(`MainRouter: DOM update for ${viewName}`);

        // 🏛️ UI Immersion: Hide global headers/nav for immersive views (crm)
        const header = document.querySelector('.top-header');
        const bottomNav = document.querySelector('.bottom-nav-container');
        const container = document.getElementById('app-views');
        const immersiveViews = ['crm'];

        if (immersiveViews.includes(viewName)) {
            if (header) (header as HTMLElement).style.display = 'none';
            if (bottomNav) (bottomNav as HTMLElement).style.display = 'none';
            if (container) container.style.paddingTop = '0';
        } else {
            if (header) (header as HTMLElement).style.display = 'flex';
            if (bottomNav) (bottomNav as HTMLElement).style.display = 'block';
            if (container) container.style.paddingTop = '56px';
        }

        document.querySelectorAll('.view-section').forEach(el => {
            el.classList.add('hidden');
            el.classList.remove('active');
            (el as HTMLElement).style.display = 'none';
        });

        const target = document.getElementById(`view-${viewName}`);
        if (target) {
            target.classList.remove('hidden');
            target.classList.add('active');
            target.style.display = 'block';
        }
    }

    private _updateNavUI(viewName: string): void {
        document.querySelectorAll('.nav-btn').forEach(btn => {
            const href = btn.getAttribute('data-spa-href');
            btn.classList.toggle('active', href === viewName);
            const icon = btn.querySelector('i');
            if (icon) {
                if (href === viewName) icon.classList.replace('ph-bold', 'ph-fill');
                else icon.classList.replace('ph-fill', 'ph-bold');
            }
        });
    }

    async handleHashChange(): Promise<void> {
        const hash = window.location.hash.replace('#', '') || 'map';
        const [base] = hash.split('/');
        if (base !== this._currentView) await this.switchView(base);
    }
}

export const mainRouter = MainRouter.getInstance();
// 🛡️ IDEMPOTENT REGISTRATION: Prevents warnings during Hot Reload
if (!registry.has('Router')) {
    registry.register('Router', mainRouter);
}
